import {
  Product,
  Cart,
  UserFilingDetails,
  CleartaxPersonalDetails,
} from '../models/product';
import { getCurrentCleartaxConfig } from '../utils/location';
import { OpResponse } from '../models/response';
import { UserAccountStatus, UserDetails } from '../models/userdetails';
import { BilldeskApiResponse } from '../models/billdeskResponse';
import {
  OrderHistoryResponse,
  OrderHistoryResponseV2,
} from '../models/orderHistory';
import { RetryPaymentLinkResponse } from '../models/retryPayment';
import { Amc, AmcDetail } from '../models/singleFundPages';
import {
  ReferralCodeResponseParent,
  ReferralSendEmailResponse,
} from '../models/referral';
import { OneTapUserServiceData } from '../models/googleYolo';
import { KYCStatusResponse } from '../models/KYCStatusResponse';
import {
  LinkMandateRequestObj,
  LinkMandateResponseObj,
} from '../models/mandate';

import 'isomorphic-unfetch';

import { Cookie, CookieJar } from 'tough-cookie';
import { showError, zeroPad } from '../utils/index';

import getConfig from 'next/config';
import CUSTOM_ERROR_CODES, { ERROR_MESSAGES } from '../constants/errorCodes';
import algoliasearch from 'algoliasearch';
import {
  googleApiScope,
  contactApiUrl,
  clientId,
  apiKey,
} from '../constants/referralConstants';

import { replicaFilterMap } from '../constants/searchConstants';

import { isMobileDevice } from '../utils/commonUtils';

const config = getConfig();

const algoliaClient = algoliasearch(
  'RIX9BSS5J6',
  '754c2d4724a28385e4227f96f05b1894'
);

/**
 * - Exposes the REST API in a typed manner
 * - Handles edge cases for server side vs client side rendering
 */
export class Api {
  public isOnServer: boolean;
  public req: any;
  public res: any;
  public cookieJar: any;
  public isUserLoggedIn: boolean;
  public baseUrl: string;
  public metaUrl: string;
  public mfPagesUrl: string;

  constructor(req?: any, res?: any) {
    this.isOnServer = req && res ? true : false;

    if (this.isOnServer) {
      this.req = req;
      this.res = res;
      const headers = req.headers || {};

      this.cookieJar = new CookieJar();
      if (headers.cookie) {
        const fullReqUrl = this.req.protocol + '://' + this.req.get('host');
        this.cookieJar.setCookieSync(headers.cookie, fullReqUrl);
      }

      const proto =
        config.publicRuntimeConfig.env === 'development' ? 'http' : 'https';
      this.baseUrl = `${proto}://${req.get('Host')}/save/api`;
      this.metaUrl = `${proto}://${req.get('Host')}/save/meta`;
      this.mfPagesUrl = `${proto}://${req.get('Host')}/save/mfpages`;
    } else {
      this.baseUrl = '/save/api';
      this.metaUrl = '/save/meta';
      this.mfPagesUrl = '/save/mfpages';
    }

    this.thenHandler = this.thenHandler.bind(this);
    this.catchHandler = this.catchHandler.bind(this);
  }

  public setCookiesInResponse() {
    if (this.isOnServer) {
      const fullReqUrl = this.req.protocol + '://' + this.req.get('host');
      const setCookieString: string[] =
        this.cookieJar.getSetCookieStringsSync(fullReqUrl);
      this.res.setHeader('Set-Cookie', setCookieString);
      return setCookieString;
    }
  }

  public async setCookie(cookie: any) {
    const fullReqUrl = this.req.protocol + '://' + this.req.get('host');
    this.cookieJar.setCookie(cookie, fullReqUrl, [], (err) => {
      if (err) {
        throw err;
      }
      this.setCookiesInResponse();
    });
  }

  private getDateRange(startDate: string) {
    let start = startDate;
    const date = new Date();
    const month = zeroPad(date.getMonth() + 1);
    const day = zeroPad(date.getDate());
    const endDate = `${date.getFullYear()}-${month}-${day}`;
    if (!startDate) {
      start = `${date.getFullYear() - 15}-${month}-${day}`;
    }

    return { start, endDate };
  }

  /**
   * When making API calls while server-side-rendering,
   * take request headers / cookies and pass them on the upstream server.
   *
   * This method should not be called on the client side.
   */
  private getHeaders(requestUrl: string, enableNewErrorResponse?: boolean) {
    const headers = { 'content-type': 'application/json' };

    headers['x-source-category'] = 'Web';

    let isMobile;
    if (this.isOnServer) {
      isMobile = isMobileDevice(this.req.headers['user-agent']);
    }
    // Confirm that the DOM is available
    else if (
      typeof navigator !== 'undefined' &&
      typeof window !== 'undefined'
    ) {
      isMobile = isMobileDevice();
    } else {
      // should not occur
    }

    headers['x-source-type'] = isMobile ? 'Mobile' : 'Desktop';

    if (this.isOnServer) {
      this.cookieJar.getCookies(requestUrl, (err, cookies) => {
        if (err) {
          return;
        }
        headers.cookie = cookies.join('; ');
      });

      const reqHeaders = this.req.headers || {};
      if (reqHeaders.hasOwnProperty('x-ctx-authentication')) {
        headers['x-ctx-authentication'] = reqHeaders['x-ctx-authentication'];
      }
    }

    if (enableNewErrorResponse) {
      headers['X-CT-New-Error-Response-Enabled'] = 'true';
    }

    return headers;
  }

  private handleCookiesFromUpstream(response: Response) {
    if (this.isOnServer) {
      const setCookie: any = response.headers.get('Set-Cookie');
      if (setCookie) {
        const cookies = Cookie.parse(setCookie);
        this.cookieJar.setCookieSync(cookies, response.url);
      }
    }
  }

  private async thenHandler(response: Response): Promise<any> {
    this.handleCookiesFromUpstream(response);

    try {
      if (response.ok) {
        return await response.json();
      } else {
        return response;
      }
    } catch (err) {
      return response;
    }
  }

  private async pennyDropThenHandler(response: Response) {
    try {
      if (response.status === 400 || response.ok) {
        return await response.json();
      } else {
        throw response;
      }
    } catch (err) {
      return response;
    }
  }

  private throwError(err: any) {
    if (!err) {
      return null;
    }
    try {
      if (typeof err !== 'string') {
        throw err.json();
      } else {
        throw err;
      }
    } catch (err1) {
      throw err;
    }
  }

  private async catchHandler(err: any) {
    const { status, statusText, url } = err;
    if (status >= 400 && status < 500) {
      if (typeof err !== 'string') {
        try {
          const errorJson = await err.json();
          return errorJson.then((error) => {
            if (error.customErrorCode) {
              showError(CUSTOM_ERROR_CODES[error.customErrorCode]);
            } else {
              showError(error.errorMessage);
            }
            throw errorJson;
          });
        } catch (e) {
          // If the error cannot be converted in a json then just throw the error directly
          throw err;
        }
      } else {
        throw err;
      }
    } else if (status >= 500 && status < 600) {
      showError(`${ERROR_MESSAGES.generic}`);
      throw { status, message: statusText, url };
    }
    this.throwError(err);
  }

  public fetchProducts(): Promise<OpResponse<Product[]>> {
    return fetch(`${this.baseUrl}/product/all`, {
      headers: this.getHeaders(`${this.baseUrl}/product/all`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public updateCart(cart: any, source: string, value: number): Promise<Cart> {
    let url = `${this.baseUrl}/cart/v2/update-cart`;
    if (source && value) {
      url = `${url}?source_type=${source}&source_value=${value}`;
    }
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(cart),
      credentials: 'include',
      headers: this.getHeaders(url, true),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getCart(): Promise<Cart> {
    return fetch(`${this.baseUrl}/cart/v2`, {
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/cart/v2`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getBucketDetails(id: any): Promise<Cart> {
    return fetch(`${this.baseUrl}/bucket/${id}`, {
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/bucket/${id}`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getUserStatusDetails(): Promise<Cart> {
    return fetch(`${this.baseUrl}/user/user-status`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/user-status`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getUserFilingDetails(): Promise<UserFilingDetails> {
    const cleartaxConfig = getCurrentCleartaxConfig();
    return fetch(
      `${this.metaUrl}/taxrecord/y${cleartaxConfig.assessmentYear}/${cleartaxConfig.entityIndex}/metadata`,
      {
        headers: this.getHeaders(
          `${this.metaUrl}/taxrecord/y${cleartaxConfig.assessmentYear}/y${cleartaxConfig.entityIndex}/metadata`
        ),
        credentials: 'include',
      }
    )
      .then(this.thenHandler)
      .then((data) => {
        const keys = Object.keys(data);
        const newObj = {};
        keys.forEach((key) => {
          const oldKey = key;
          if (oldKey[0] !== '_') {
            newObj[oldKey[0].toUpperCase() + oldKey.slice(1, oldKey.length)] =
              data[key];
          } else {
            newObj[oldKey] = data[key];
          }
        });
        return newObj;
      })
      .catch(this.catchHandler);
  }

  public getUserPersonalDetails(): Promise<
    OpResponse<CleartaxPersonalDetails>
  > {
    const cleartaxConfig = getCurrentCleartaxConfig();
    return fetch(
      `${this.metaUrl}/entity/y${cleartaxConfig.assessmentYear}/${cleartaxConfig.entityIndex}/personaladdressinfo`,
      {
        headers: this.getHeaders(
          `${this.metaUrl}/entity/y${cleartaxConfig.assessmentYear}/${cleartaxConfig.entityIndex}/personaladdressinfo`
        ),
        credentials: 'include',
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getUserDetails(isApiValidate?: boolean): Promise<UserDetails> {
    const query = isApiValidate ? '?validation-group=PRE_PAYMENT_CHECKS' : '';
    return fetch(`${this.baseUrl}/user/user-details${query}`, {
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/user-details${query}`),
    })
      .then((res) => {
        this.handleCookiesFromUpstream(res);
        return res.json();
      })
      .catch(this.catchHandler);
  }

  public downloadPrefilledNachForm(maxAmount: number) {
    return fetch(
      `${this.baseUrl}/user/mandate/paper-mandate/form?maxAmount=${maxAmount}`
    );
  }

  public updateUserDetails(
    data: any,
    validationKey: string
  ): Promise<UserDetails> {
    const query = validationKey ? `?validation-group=${validationKey}` : '';
    return fetch(`${this.baseUrl}/user/user-details${query}`, {
      credentials: 'include',
      method: 'PUT',
      headers: this.getHeaders(`${this.baseUrl}/user/user-details${query}`),
      body: JSON.stringify(data),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public saveUserDetails(patch: any): Promise<UserDetails> {
    return fetch(`${this.baseUrl}/user/user-details`, {
      method: 'POST',
      body: JSON.stringify(patch),
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/user-details`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getCurrentUser(): Promise<any> {
    return fetch(`${this.baseUrl}/user/current`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/current`),
    })
      .then((res) => {
        this.handleCookiesFromUpstream(res);
        return res.json();
      })
      .catch((err) => {
        // tslint:disable-next-line:no-console
        console.log("unauthorized",{err})
      }); // tslint:disable-line
  }

  public getRentReceipt(formData: any): Promise<any> {
    return fetch(`${this.baseUrl}/rent/get-rent-receipt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.ok) {
          return response;
        } else {
          return { status: response.status };
        }
      })
      .catch(this.catchHandler);
  }

  public placeOrder(
    paymentMode: string,
    token: string
  ): Promise<BilldeskApiResponse> {
    return fetch(`${this.baseUrl}/user/placeOrder/${paymentMode}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...this.getHeaders(`${this.baseUrl}/user/placeOrder/${paymentMode}`),
        'xpe-investment-token': token,
      },
    })
      .then(this.thenHandler)
      .catch((err) => {
        showError(ERROR_MESSAGES.generic);
        throw err;
      });
  }

  public placeOrderV1(
    paymentGateway: string,
    paymentMode: string,
    data: object
  ): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/placeOrderV1/${paymentGateway}/${paymentMode}/0`,
      {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(data),
        headers: this.getHeaders(
          `${this.baseUrl}/user/placeOrderV1/${paymentGateway}/${paymentMode}/0`
        ),
      }
    )
      .then(this.thenHandler)
      .catch((err) => {
        showError(
          'Something went wrong. Please reach us at 080-67458744 or invest@cleartax.in'
        );
        throw err;
      });
  }

  public placeOrderV2(
    paymentGateway: string,
    paymentMode: string,
    data: object,
    bankAccId: number,
    token: string
  ): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/placeOrderV1/${paymentGateway}/${paymentMode}/0?version=V2&bankDetailsId=${bankAccId}`,
      {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(data),
        headers: {
          ...this.getHeaders(
            `${this.baseUrl}/user/placeOrderV1/${paymentGateway}/${paymentMode}/0`
          ),
          'xpe-investment-token': token,
        },
      }
    )
      .then(this.thenHandler)
      .catch((err) => {
        showError(
          'Something went wrong. Please reach us at 080-67458744 or invest@cleartax.in'
        );
        throw err;
      });
  }

  public getIfscInfo(ifsc: string): Promise<any> {
    return fetch(`/save/ifsc/${ifsc}`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public lookupPincode(pincode: string): Promise<any> {
    return fetch(`${this.baseUrl}/user/pin-code-data?pincode=${pincode}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public handleKycCheck(data: any, page: string): Promise<KYCStatusResponse> {
    return fetch(`${this.baseUrl}/third-party/checkKycStatus/v1?page=${page}`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(data),
      headers: this.getHeaders(`${this.baseUrl}/third-party/checkKycStatus/v1`),
    })
      .then(this.thenHandler)
      .catch((err) => {
        const { status } = err;
        // suppress 500 error
        if (status >= 500 && status < 600) {
          console.log(err); //tslint:disable-line
        } else {
          this.catchHandler(err);
        }
      });
  }

  public getPaymentData(externalId: string): Promise<any> {
    return fetch(`${this.baseUrl}/user/getPayment?externalId=${externalId}`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/user/getPayment?externalId=${externalId}`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public findUser(email: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/auth/find?email=${email.toLowerCase()}`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/v1/auth/find?email=${email.toLowerCase()}`
      ),
    })
      .then(this.thenHandler)
      .catch((err) => {
        if (err.status === 400) {
          throw err;
        } else {
          this.catchHandler(err);
        }
      });
  }

  public login(data: any): Promise<any> {
    const { email, password } = data;
    return fetch(`${this.baseUrl}/v1/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/v1/auth/login`),
      body: JSON.stringify({
        contact_string: email.toLowerCase(),
        password,
      }),
    })
      .then(this.thenHandler)
      .catch((err) => {
        if (err.status === 401) {
          throw err;
        } else {
          this.catchHandler(err);
        }
      });
  }

  public googleLogin(code: string): Promise<any> {
    return (
      fetch(`${this.baseUrl}/v1/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: this.getHeaders(`${this.baseUrl}/v1/auth/login`),
        body: JSON.stringify({
          code,
          login_type: 'GOOGLE',
        }),
      })
        .then((res) => {
          if (this.isOnServer) {
            const setCookie = res.headers.get('Set-Cookie');
            if (setCookie) {
              this.res.setHeader('Set-Cookie', setCookie);
            }
          }
        })
        // .then(this.thenHandler)
        .catch(this.catchHandler)
    );
  }

  public createAccount(data: any): Promise<any> {
    const { email, password } = data;
    return fetch(`${this.baseUrl}/v1/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/v1/auth/register`),
      body: JSON.stringify({
        contact_string: email.toLowerCase(),
        password,
      }),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public sendOtp(phone: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/v1/auth/user/actions/send-password-less-login-token?phone_number=${phone}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/v1/auth/user/actions/send-password-less-login-token?phone_number=${phone}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public submitOtp(data: any): Promise<any> {
    const { otp, phone } = data;
    return fetch(`${this.baseUrl}/v1/auth/user/actions/login/password-less`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/v1/auth/user/actions/login/password-less`
      ),
      body: JSON.stringify({
        phoneNumber: phone,
        token: otp,
      }),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public generateVerificationCode(phone: string): Promise<any> {
    return fetch(`${this.baseUrl}/v1/auth/user/contacts`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/v1/auth/user/contacts`),
      body: JSON.stringify({
        contactType: 'MOBILE',
        contactDetail: phone,
      }),
    })
      .then(this.thenHandler)
      .then(() => {
        return fetch(
          `${this.baseUrl}/v1/auth/user/contacts/send-verification-sms?phone_number=${phone}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: this.getHeaders(
              `${this.baseUrl}/v1/auth/user/contacts/send-verification-sms?phone_number=${phone}`
            ),
          }
        );
      })
      .then(this.thenHandler)
      .catch((err) => {
        if (err.status === 409) {
          throw err.json();
        } else {
          return this.catchHandler(err);
        }
      });
  }

  public resendOtp(phone: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/v1/auth/user/contacts/send-verification-sms?phone_number=${phone}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/v1/auth/user/contacts/send-verification-sms?phone_number=${phone}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public verifyPhone(data: any): Promise<any> {
    const { otp, phone } = data;
    return fetch(`${this.baseUrl}/v1/auth/user/contacts/verify-phone-number`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/v1/auth/user/contacts/verify-phone-number`
      ),
      body: JSON.stringify({
        phoneNumber: phone,
        token: otp,
      }),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public logout(): Promise<any> {
    return fetch(`${this.baseUrl}/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/v1/auth/logout`),
    })
      .then((res) => {
        if (res.ok) {
          this.handleCookiesFromUpstream(res);
          return res.json();
        } else {
          throw res;
        }
      })
      .catch(this.catchHandler);
  }

  public getGoogleAuthCallback(state: any): Promise<any> {
    return fetch(
      `${this.baseUrl}/v1/auth/google-auth?state=${btoa(
        JSON.stringify(state)
      )}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/v1/auth/google-auth?state=${btoa(
            JSON.stringify(state)
          )}`
        ),
      }
    ).then((res) => {
      this.handleCookiesFromUpstream(res);
      return res.text();
    });
  }

  public createMandate(data: any, path: string): Promise<any> {
    const body = getFormData(data);
    return fetch(`${this.baseUrl}/user/mandate/${path}`, {
      method: 'POST',
      credentials: 'include',
      body,
    }).then(this.thenHandler);
  }

  public getMandateInfo(useCase: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/mandate/request-mandate-info/0?useCase=${useCase}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/user/mandate/request-mandate-info/0?useCase=${useCase}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getMandateRequiredSips(): Promise<any> {
    return fetch(`${this.baseUrl}/user/mandate/mandate-required-sips`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/user/mandate/mandate-required-sips`
      ),
    }).then(this.thenHandler);
  }

  public getMandatesList(): Promise<any> {
    return fetch(`${this.baseUrl}/user/mandate/all-mandates`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/mandate/all-mandates`),
    }).then(this.thenHandler);
  }

  public enableMonthlyLinkOnly(): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/mandate/edit-user-preference?user-preference=MONTHLY_LINKS_ONLY&is-ticked=true`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/user/mandate/edit-user-preference?user-preference=MONTHLY_LINKS_ONLY&is-ticked=true`
        ),
      }
    ).catch(() => {
      // console.log(err)
    });
  }

  public getAccountStatus(): Promise<UserAccountStatus> {
    return fetch(`${this.baseUrl}/rzp/fav/accountStatus`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/rzp/fav/accountStatus`),
    }).then(this.thenHandler);
  }

  public postAccountDetails(data: any, extraParams: string = ''): Promise<any> {
    return fetch(`${this.baseUrl}/rzp/fav/accountDetails${extraParams}`, {
      method: 'POST',
      credentials: 'include',
      body: getFormData(data),
    })
      .then(this.thenHandler)
      .catch((err) => err.json());
  }

  public getChildOrders(key: string, id: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/dashboard/order-intent-details?${key}=${id}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/dashboard/order-intent-details?${key}=${id}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getOrderHistory(): Promise<OrderHistoryResponse> {
    return fetch(`${this.baseUrl}/dashboard/order-history/0`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/dashboard/order-history`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getOrderHistoryV2(): Promise<OrderHistoryResponseV2> {
    return fetch(`${this.baseUrl}/dashboard/order-history/v2/0`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/dashboard/order-history/v2`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getRetryPaymentLink(
    orderId: string
  ): Promise<RetryPaymentLinkResponse> {
    return fetch(`${this.baseUrl}/user/payment/retry/0/${orderId}`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/user/payment/retry/0/${orderId}`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getTransactions(): Promise<any> {
    return fetch(`${this.baseUrl}/dashboard/investment-transactions/v1`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/dashboard/investment-transactions/v1`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getFundTransactions(
    folioNum?: string,
    prodId?: number,
    type?: string
  ): Promise<any> {
    let url = `${this.baseUrl}/dashboard/transactions`;
    if (folioNum && prodId) {
      url = `${url}?folioNumber=${folioNum}&productId=${prodId}`;
    }
    if (type) {
      url = `${url}?trxn_category=${type}`;
    }
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getSipOrders(): Promise<any> {
    return fetch(
      `${this.baseUrl}/dashboard/sip-details/v2?includePrimeSip=true`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/dashboard/sip-details?includePrimeSip=true`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public downloadInvestmentProof(fy: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/generator/get-provisional-statement-v3/${fy}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/generator/get-provisional-statement-v3/${fy}`
        ),
      }
    )
      .then((response) => {
        if (response.ok) {
          response.blob().then((blob) => {
            this.handleDownloadFileResponse(
              blob,
              'CleartaxInvestmentProof.zip'
            );
          });
        } else {
          return { status: response.status };
        }
      })
      .catch(this.catchHandler);
  }

  public handleDownloadFileResponse(blob: any, name: string) {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = name;
    // the element needs to be appeded to body for it to be able to click in firefox
    document.body.appendChild(link);
    link.click();
    // remove the element after some time
    setTimeout(() => {
      link.remove();
    }, 5000);
  }

  public getInactiveSips(): Promise<any> {
    return fetch(`${this.baseUrl}/user/mandate/mandate-required-sips`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/user/mandate/mandate-required-sips`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public placeRedeemOrder(token: string): Promise<any> {
    return fetch(`${this.baseUrl}/user/placeOrder/NO_PAYMENT`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...this.getHeaders(`${this.baseUrl}/user/placeOrder/NO_PAYMENT`),
        'xpe-redemption-token': token,
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public pauseOrResumeSip(
    externalId: string,
    id: number,
    action: string
  ): Promise<any> {
    const url = `${this.baseUrl}/dashboard/order-intent-details/${externalId}/items/${id}/${action}`;
    return fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getFundDetails(id: string | number, type?: string): Promise<any> {
    let query = `/${id}`;
    if (type && type === 'ISIN') {
      query = `?${id}`;
    }
    return fetch(`${this.baseUrl}/investmentProducts${query}`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/investmentProducts/${id}`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getFundDetailsByIsin(isin: string): Promise<any> {
    return fetch(`${this.baseUrl}/investmentProducts/isin/${isin}`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/investmentProducts/isin/${isin}`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getAllAmcs(): Promise<Amc[]> {
    return fetch(`${this.mfPagesUrl}/amc`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getSingleAmc(amc: string): Promise<AmcDetail> {
    return fetch(`${this.mfPagesUrl}/amc/${amc}/`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getSingleAmcBasicData(amc: string): Promise<AmcDetail> {
    return fetch(`${this.mfPagesUrl}/amc/${amc}/amcBasicsData`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getFundById(fundId: string, idType: string) {
    return fetch(`${this.mfPagesUrl}/funds/${fundId}?id_type=${idType}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      mode: 'no-cors',
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getAllInvestmentProducts() {
    const url = `${this.baseUrl}/investmentProducts`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public searchFund(
    query: string,
    page: number,
    facetFilters: any,
    sortBy: string,
    hitsPerPage: number = 20
  ) {
    const index = algoliaClient.initIndex(sortBy);
    const defaultFilters = replicaFilterMap[sortBy];
    const searchParams: any = {
      query,
      page,
      hitsPerPage,
      facetFilters,
      filters: defaultFilters,
    };
    return index
      .search(searchParams)
      .then((res) => res)
      .catch(this.catchHandler);
  }

  public searchTPVBank(query: string, sortBy: string) {
    const index = algoliaClient.initIndex(sortBy);
    const defaultFilters = replicaFilterMap[sortBy];
    return index
      .search({
        query,
        hitsPerPage: 20,
        filters: defaultFilters,
      })
      .then((res) => res)
      .catch(this.catchHandler);
  }

  public getMergerData(isin: string, sortBy: string) {
    const index = algoliaClient.initIndex(sortBy);
    // const defaultFilters = replicaFilterMap[sortBy];
    return index
      .search({
        hitsPerPage: 20,
        facetFilters: [[`SwitchInISIN:${isin}`]],
      })
      .then((res) => res)
      .catch(this.catchHandler);
  }

  public getDirectLogin(token: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/v1/auth/user/action/login/direct_login?auth_token=${token}`,
      {
        method: 'POST',
        credentials: 'include',
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getReferCode(): Promise<ReferralCodeResponseParent> {
    return fetch(`${this.baseUrl}/referral/referral-code`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/referral/referral-code`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public sendEmails(emails: string[]): Promise<ReferralSendEmailResponse> {
    return fetch(`${this.baseUrl}/referral/refer`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/refer`),
      body: JSON.stringify({
        emails,
      }),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getGmailContacts(): Promise<any> {
    // tslint:disable-next-line
    return new Promise((resolve, reject) => {
      const scopes = googleApiScope;

      gapi.client.setApiKey(apiKey);
      gapi.auth.authorize(
        { client_id: clientId, scope: scopes, immediate: false },
        (authorizationResult) => {
          if (authorizationResult && !authorizationResult.error) {
            fetch(
              `${contactApiUrl}${authorizationResult.access_token}&max-results=5000&v=3.0`
            )
              .then((res) => resolve(res.json()))
              .catch(this.catchHandler);
          }
        }
      );
    });
  }

  public fetchAadhaarVerification(adharNo: string) {
    return fetch(`${this.baseUrl}/third-party/birlaValidateAdharNo`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/third-party/birlaValidateAdharNo`
      ),
      body: JSON.stringify({
        adharNo,
      }),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public fetchOTPVerification(adharNo: string, otp: string, refId: string) {
    return fetch(`${this.baseUrl}/third-party/birlaValidateAdharOTP`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/third-party/birlaValidateAdharOTP`
      ),
      body: JSON.stringify({ adharNo, otp, refId }),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public createBirlaFolio(payload: any) {
    return fetch(`${this.baseUrl}/third-party/createBirlaFolio`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/third-party/createBirlaFolio`),
      body: JSON.stringify(payload),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getCkycForm(): Promise<any> {
    return fetch(`${this.baseUrl}/generator/generate-kyc-form`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'content-type': 'application/pdf',
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public postCkycData(data: any): Promise<any> {
    return fetch(`${this.baseUrl}/user/ckyc`, {
      method: 'POST',
      credentials: 'include',
      body: getFormData(data),
    })
      .then(this.thenHandler)
      .catch((err) => err.json());
  }

  public getCkycData(): Promise<any> {
    return fetch(`${this.baseUrl}/user/ckyc`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public postSignzyOnboardCustomer(): Promise<any> {
    return fetch(`${this.baseUrl}/signzy/onboardCustomer`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/signzy/onboardCustomer`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public authenticateGoogleIdToken(data: OneTapUserServiceData): Promise<any> {
    return fetch(`${this.baseUrl}/v1/auth/user/one-touch-login`, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/v1/auth/user/one-touch-login`),
      body: JSON.stringify(data),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getPendingActions(): Promise<any> {
    return fetch(`${this.baseUrl}/user/pending-actions`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public loadStockData(fundId: string, startDate: string): Promise<any> {
    const { start, endDate } = this.getDateRange(startDate);
    return fetch(
      `${this.mfPagesUrl}/funds/${fundId}/historical-nav-performance?start_date=${start}&end_date=${endDate}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        mode: 'no-cors',
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public loadCategoryData(isin: string, startDate: string): Promise<any> {
    const { start, endDate } = this.getDateRange(startDate);
    return fetch(
      `${this.mfPagesUrl}/category/${isin}/nav-performance?start_date=${start}&end_date=${endDate}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        mode: 'no-cors',
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public fetchSimilarFunds(isin: string, criteria: string): Promise<any> {
    return fetch(
      `${this.mfPagesUrl}/recommendations/funds/${isin}?criteria=${criteria}`
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public linkSipToMandate(
    data: LinkMandateRequestObj[]
  ): Promise<LinkMandateResponseObj[]> {
    return fetch(`${this.baseUrl}/user/mandate/link-sips-to-mandates`, {
      method: 'PUT',
      body: JSON.stringify(data),
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/user/mandate/link-sips-to-mandates`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public downloadMonthlyStatement(
    startDate: string,
    endDate: string
  ): Promise<any> {
    return fetch(
      `${this.baseUrl}/generator/generate-investment-statement/0?start_date=${startDate}&end_date=${endDate}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/generator/generate-investment-statement/0?start_date=${startDate}&end_date=${endDate}`
        ),
      }
    )
      .then((response) => {
        if (response.ok) {
          response.blob().then((blob) => {
            this.handleDownloadFileResponse(blob, 'investment-statement.pdf');
          });
        } else {
          return { status: response.status };
        }
      })
      .catch(this.catchHandler);
  }

  public getChatState(id: string): Promise<any> {
    return fetch(`${this.baseUrl}/chat/state?id=${id}`, {
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/chat/state?id=${id}`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getElssInvestedYears(): Promise<any> {
    return fetch(`${this.baseUrl}/user/elss-investment-years`, {
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/elss-investment-years`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getIncomplePayementCartDetails(): Promise<any> {
    return fetch(`${this.baseUrl}/user/complete-payment`, {
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/complete-payment`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public send80cData(data: any): Promise<any> {
    return fetch(`${this.baseUrl}/chat/filing/consumer`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(data),
      headers: this.getHeaders(`${this.baseUrl}/chat/filing/consumer`),
    }).catch((err) => {
      const { status } = err;
      // suppress 500 error
      if (status >= 500 && status < 600) {
        console.log(err); //tslint:disable-line
      } else {
        this.catchHandler(err);
      }
    });
  }

  public getGraphData(
    startDate: string,
    endDate: string,
    isinCodeArr: any
  ): Promise<any> {
    const listOfFunds = isinCodeArr.join('&listOfFunds=');
    return fetch(
      `${this.mfPagesUrl}/plan/historical-plan-performance?start_date=${startDate}&end_date=${endDate}&listOfFunds=${listOfFunds}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        mode: 'no-cors',
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public whatsAppCommunication(whatsAppCommunicationObject: {
    mobileNumber: number;
    templateName: string;
  }): Promise<any> {
    return fetch(`${this.baseUrl}/chat/f2Scommunication`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(whatsAppCommunicationObject),
      headers: this.getHeaders(`${this.baseUrl}/chat/f2Scommunication`),
    }).catch((err) => {
      showError(
        'Something went wrong. Please reach us at 080-67458744 or invest@cleartax.in'
      );
      throw err;
    });
  }

  public sendGoalBasedData(goalBasedData: {
    phoneNo: number;
    goal: string;
    targetAmount: number;
    savedAmount: number;
    years: number;
  }): Promise<any> {
    return fetch(`${this.baseUrl}/chat/goalBased/consumer`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(goalBasedData),
      headers: this.getHeaders(`${this.baseUrl}/chat/goalBased/consumer`),
    }).catch((err) => {
      showError(
        'Something went wrong. Please reach us at 080-67458744 or invest@cleartax.in'
      );
      throw err;
    });
  }

  public getSipDates(): Promise<any> {
    return fetch(`${this.baseUrl}/dashboard/sip-details/v2/get-sip-dates`, {
      credentials: 'include',
      headers: this.getHeaders(
        `${this.baseUrl}/dashboard/sip-details/v2/get-sip-dates`
      ),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public editSipDate(dateOfMonth: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/dashboard/sip-details/edit-sip-date/${dateOfMonth}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/dashboard/sip-details/edit-sip-date/${dateOfMonth}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  // useCase: REGULAR or DIRECT
  public processRazorpayMandate(
    paymentId: string,
    useCase: string
  ): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/mandate/processMandate/razorpay/${useCase}`,
      {
        method: 'POST',
        credentials: 'include',
        // because of content type, body is structured in this way. DO NOT give space anywhere in string
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: `razorpay_payment_id=${paymentId}`,
      }
    )
      .then(this.thenHandler)
      .catch((err) => err.json());
  }

  public schedulePayment(data: any): Promise<any> {
    return fetch(`${this.baseUrl}/user/scheduleOrder/v1/1`, {
      method: 'POST',
      credentials: 'include',
      body: getFormData(data),
    })
      .then(this.thenHandler)
      .catch((err) => err.json());
  }

  public sendBlackSMS(phone: string, cb: any): Promise<any> {
    return fetch('https://cleartax.in/f/kramer/black-sms', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone_number: phone }),
    })
      .then((res) => {
        if (res.ok) {
          this.thenHandler(res);
          cb(true);
        }
        this.thenHandler(res);
      })
      .catch(this.catchHandler);
  }

  public recordBlackSMSSheet(phone: string): Promise<any> {
    // TODO: Remove URL hardcoding
    const userData = {
      CTA_Category: 'topCTA',
      Mobile_number: phone,
      Date: new Date(new Date()).toDateString(),
      Time: new Date(new Date()).toLocaleTimeString(),
      Page: 'https://cleartax.in/save',
      Type: 'black-save-web',
    };

    let paramStr = '?';
    Object.keys(userData).forEach((item) => {
      paramStr += `${item}=${userData[item]}&`;
    });

    return fetch(
      'https://script.google.com/macros/s/AKfycbyExCXwCBFZNlealVZnrzP90HjgbFiJ3twqh_9gKD6gV9C6CtQ/exec' +
        paramStr.toString().slice(0, -1)
    );
  }

  public getUserInvestmentInProduct(productId: number): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/invested-in-Product?product-id=${productId}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/user/invested-in-Product?product-id=${productId}`
        ),
      }
    )
      .then((response) => {
        if (response.ok) {
          return response;
        }
      })
      .catch(this.catchHandler);
  }

  public getTotalSipAmount(prodId: number): Promise<any> {
    return fetch(
      `${this.baseUrl}/dashboard/fund/total-sip-amount/0?prodId=${prodId}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/dashboard/fund/total-sip-amount/0?prodId=${prodId}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public initializePennyDrop(
    bankDetails: {
      accountNo: string;
      ifscCode: string;
      cityOfBank: string;
      nameOfBank: string;
      branchName: string;
    },
    extraParams: string = ''
  ): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/bank-accounts/penny-drop${extraParams}`,
      {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(bankDetails),
        headers: this.getHeaders(
          `${this.baseUrl}/user/bank-accounts/penny-drop`
        ),
        // mode: 'no-cors',
      }
    )
      .then(this.pennyDropThenHandler)
      .catch(this.catchHandler);
  }

  public initializePennyDropWithSource(
    source: string = 'REDEMPTION'
  ): Promise<any> {
    return fetch(`${this.baseUrl}/user/pennyDrop?source=${source}`, {
      method: 'POST',
      credentials: 'include',
      // body: JSON.stringify(bankDetails),
      headers: this.getHeaders(
        `${this.baseUrl}/user/pennyDrop?source=${source}`
      ),
      // mode: 'no-cors',
    })
      .then(this.pennyDropThenHandler)
      .catch(this.catchHandler);
  }

  public getPennyDropStatus(accountNo: string, ifsc: string): Promise<any> {
    return fetch(
      `${this.baseUrl}/user/bank-accounts/penny-drop/status?accountNo=${accountNo}&ifscCode=${ifsc}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: this.getHeaders(
          `${this.baseUrl}/user/bank-accounts/penny-drop/status?accountNo=${accountNo}&ifscCode=${ifsc}`
        ),
      }
    )
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getUserPennyDropStatus(): Promise<any> {
    return fetch(`${this.baseUrl}/user/pennyDrop/status`, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(`${this.baseUrl}/user/pennyDrop/status`),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getInvestedTransactions(
    folioNumber: number,
    productId: number
  ): Promise<any> {
    const url = `${this.baseUrl}/dashboard/transactions/v2/0?folioNumber=${folioNumber}&productId=${productId}`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getSimilarFunds(isinCode: string): Promise<any> {
    const url = `${this.mfPagesUrl}/recommendations/funds/v2/${isinCode}?batch-size=10`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getRedemptionDetails(
    folioNumber: number,
    productId: number
  ): Promise<any> {
    const url = `${this.baseUrl}/dashboard/redemption-details/0?folioNumber=${folioNumber}&productId=${productId}&fetchOngoingOrders=true`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getCurrentUserDetails(): Promise<any> {
    const url = `${this.baseUrl}/user/current/0`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch((err) => {
        // tslint:disable-next-line:no-console
        console.log("unauthorized",{err})
        this.catchHandler(err);
      });
  }

  public postAcceptedPolicies(acceptedPolicies: any): Promise<any> {
    const url = `${this.baseUrl}/v1/auth/user/accept-policies`;
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify({ acceptedPolicies }),
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getLatestPolicies(): Promise<any> {
    const url = `${this.baseUrl}/v1/auth/get-latest-policies`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public addBankAccount(bankDetails: {
    accountNo: string;
    ifscCode: string;
    cityOfBank: string;
    nameOfBank: string;
    branchName: string;
  }): Promise<any> {
    const url = `${this.baseUrl}/user/bank-accounts/add-bank-account`;
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(bankDetails),
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getAllBankAccounts(): Promise<any> {
    const url = `${this.baseUrl}/user/bank-accounts/get-all-bank-accounts`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public deleteBankAccount(bankDetails: {
    accountNo: string;
    ifscCode: string;
    cityOfBank: string;
    nameOfBank: string;
    branchName: string;
  }): Promise<any> {
    const url = `${this.baseUrl}/user/bank-accounts/delete-bank-account`;
    return fetch(url, {
      method: 'PUT',
      body: JSON.stringify(bankDetails),
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public changePrimaryBankAccount(bankDetails: {
    accountNo: string;
    ifscCode: string;
    cityOfBank: string;
    nameOfBank: string;
    branchName: string;
  }): Promise<any> {
    const url = `${this.baseUrl}/user/bank-accounts/change-primary-bank-account`;
    return fetch(url, {
      method: 'PUT',
      body: JSON.stringify(bankDetails),
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getMandateStatus(amount: number, id: number): Promise<any> {
    const url = `${this.baseUrl}/user/mandate/mandate-available?amount=${amount}&usecase=DIRECT&bankDetailsId=${id}`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch((err) => {
        const { status } = err;
        try {
          if (status === 404) {
            return err;
          } else {
            throw err;
          }
        } catch (e) {
          throw err;
        }
      });
  }

  public cancelScheduleOrder(orderExternalId: string): Promise<any> {
    const url = `${this.baseUrl}/dashboard/cancelScheduledOrder/${orderExternalId}/0`;
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }
  public cancelSipOrder(orderExternalId: string, itemId: number): Promise<any> {
    const url = `${this.baseUrl}/dashboard/cancel-sip/${orderExternalId}/items/${itemId}/0`;
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }
  public handleOtp(
    data: any,
    isVerify: boolean,
    isInstantRedemption: boolean = false
  ): Promise<any> {
    const instantRedemptionParam = isInstantRedemption
      ? '?instant-redemption=true'
      : '';
    const url = !isVerify
      ? `${this.baseUrl}/user/send-redemption-otp/0${instantRedemptionParam}`
      : `${this.baseUrl}/user/verify-redemption-otp/0${instantRedemptionParam}`;
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(url),
      body: JSON.stringify(data),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public handleInvestmentOtp(data: any, isVerify: boolean): Promise<any> {
    const url = !isVerify
      ? `${this.baseUrl}/user/send-investment-otp/0`
      : `${this.baseUrl}/user/verify-investment-otp/0`;
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(url),
      body: JSON.stringify(data),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public requireInvestmentOtp(productIds: any): Promise<any> {
    const url = `${this.baseUrl}/user/require-investment-otp/0`;
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: this.getHeaders(url),
      body: JSON.stringify(productIds),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getAmcFaqs() {
    const url = `${this.mfPagesUrl}/amc_basics/faqs`;
    // return [
    //   {
    //     question: 'What is a mutual fund?',
    //     answer:
    //       'A mutual fund is a pooled investment vehicle in which money from multiple investors is invested in a diversified portfolio of stocks, bonds, or other securities.',
    //   },
    //   {
    //     question: 'Why should I invest in mutual funds?',
    //     answer:
    //       'Mutual funds offer diversification, professional management, liquidity, and accessibility, making them ideal for beginners and experienced investors.',
    //   },
    //   {
    //     question: 'How do mutual funds work?',
    //     answer:
    //       'Investors buy units of a mutual fund, which are then used to invest in a portfolio of securities. The value of the units (NAV) changes based on the performance of the investments.',
    //   },
    //   {
    //     question: 'What is an AMC (Asset Management Company)?',
    //     answer:
    //       'An AMC is a company that manages mutual funds. It is responsible for pooling investor money, making investment decisions, and managing the portfolio.',
    //   },
    //   {
    //     question: 'What is NAV (Net Asset Value)?',
    //     answer:
    //       'NAV represents the price per unit of a mutual fund and is calculated daily by dividing the total value of the fund’s assets minus liabilities by the number of outstanding units.',
    //   },
    //   {
    //     question: 'What is SIP (Systematic Investment Plan)?',
    //     answer:
    //       'SIP is a method where investors contribute a fixed sum of money regularly (monthly or quarterly) to invest in mutual funds, allowing for disciplined investing.',
    //   },
    //   {
    //     question: 'Can I start an SIP with ₹500?',
    //     answer:
    //       'Yes, many mutual funds allow SIP investments starting from as low as ₹500, making it accessible for everyone.',
    //   },
    //   {
    //     question: 'Can I invest a lump sum amount in mutual funds?',
    //     answer:
    //       'You can make a one-time lump sum investment in mutual funds based on your financial goals.',
    //   },
    // ];
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getAmcEmail(slug: string): Promise<string> {
    const url = `${this.mfPagesUrl}/amc_basics/email/${slug}`;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then((response) => {
        return response.text();
      })
      .catch(this.catchHandler);
  }

  public getFundSchemaDetails(isin: string) {
    const url = `${this.mfPagesUrl}/funds/schema/` + isin;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }

  public getFundDistribution(isin: string) {
    const url = `${this.mfPagesUrl}/fund_distribution/` + isin;
    return fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: this.getHeaders(url),
    })
      .then(this.thenHandler)
      .catch(this.catchHandler);
  }
}
/* TODO: define interface for object */
function getFormData(object: any) {
  const formData = new FormData();
  Object.keys(object).forEach((key) => formData.append(key, object[key]));
  return formData;
}



// WEBPACK FOOTER //
// ./api/index.ts